class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 280);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static syncFacetState() {
    document.querySelectorAll('[data-prea-price-range]').forEach((range) => {
      range.querySelectorAll('[data-prea-price-range-input]').forEach((input) => {
        input.dispatchEvent(new Event('prea:range-sync'));
      });
    });
    document.dispatchEvent(new CustomEvent('prea:search-facets-updated'));
  }

  static clearAllFilters() {
    const form = document.getElementById('FacetFiltersForm');
    if (form) {
      form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
      form.querySelectorAll('input[data-prea-price-input]').forEach((inp) => {
        inp.value = '';
      });
      form.querySelectorAll('input[data-prea-price-range-input]').forEach((rng) => {
        rng.value = rng.getAttribute('data-prea-price-range-input') === 'min' ? rng.min : rng.max;
        rng.dispatchEvent(new Event('prea:range-sync'));
      });
    }
    const sortInput = document.getElementById('SortBy');
    const sortVal = sortInput ? sortInput.value : '';
    const nextParams = new URLSearchParams(form?.dataset.clearUrl?.split('?')[1] || '');
    nextParams.delete('page');
    if (sortVal) {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has('sort_by')) {
        nextParams.set('sort_by', sortVal);
      }
    }
    FacetFiltersForm.renderPage(nextParams.toString(), null, true);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = window.location.search.slice(1);
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    document.addEventListener('click', (event) => {
      const clearAllTrigger = event.target.closest('[data-prea-clear-all]');
      if (clearAllTrigger) {
        event.preventDefault();
        event.stopPropagation();
        FacetFiltersForm.clearAllFilters();
        return;
      }

      const paginationLink = event.target.closest('.collection-grid-section .prea-search-pagination a.prea-search-pagination__item');
      if (!paginationLink || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const href = paginationLink.getAttribute('href');
      if (!href) return;
      event.preventDefault();
      const searchParams = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(searchParams, null, true);
      const gridSection = document.querySelector('.collection-grid-section');
      if (gridSection) {
        const rect = gridSection.getBoundingClientRect();
        const scrollTop = window.pageYOffset + rect.top - 96;
        window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const clearAllTrigger = event.target.closest('.prea-sidebar-header__clear[data-prea-clear-all]');
      if (!clearAllTrigger) return;
      event.preventDefault();
      event.stopPropagation();
      FacetFiltersForm.clearAllFilters();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        FacetFiltersForm.syncFacetState();
      });
    } else {
      FacetFiltersForm.syncFacetState();
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const params = new URLSearchParams(searchParams);
    params.delete('section_id');
    params.delete('sections');
    const normalizedParams = params.toString();
    const section = FacetFiltersForm.getSections()[0];
    if (!section) return;

    const pageUrl = new URL(window.location.pathname, window.location.origin);
    pageUrl.search = normalizedParams;
    const sectionUrl = new URL(pageUrl);
    sectionUrl.searchParams.set('section_id', section.section);
    const cacheKey = sectionUrl.toString();

    FacetFiltersForm.requestController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.requestController = controller;
    const requestId = ++FacetFiltersForm.requestId;
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', 'true');
    grid?.querySelector('.collection')?.classList.add('loading');
    document.getElementById('ProductCount')?.classList.add('loading');

    try {
      let html = FacetFiltersForm.filterData.get(cacheKey);
      if (!html) {
        const response = await fetch(sectionUrl, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to load collection results');
        html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById('ProductGridContainer') || !parsed.getElementById('product-grid')) {
          throw new Error('Invalid collection response');
        }
        if (requestId !== FacetFiltersForm.requestId) return;
        FacetFiltersForm.filterData.set(cacheKey, html);
        if (FacetFiltersForm.filterData.size > 10) {
          FacetFiltersForm.filterData.delete(FacetFiltersForm.filterData.keys().next().value);
        }
      }
      if (requestId !== FacetFiltersForm.requestId) return;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      FacetFiltersForm.searchParamsPrev = normalizedParams;
      if (updateURLHash && window.location.search.slice(1) !== normalizedParams) {
        FacetFiltersForm.updateURLHash(normalizedParams);
      }
      FacetFiltersForm.syncFacetState();
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === FacetFiltersForm.requestId) {
        window.location.assign(pageUrl.toString());
      }
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        document.getElementById('ProductGridContainer')?.removeAttribute('aria-busy');
        document.querySelector('#ProductGridContainer .collection')?.classList.remove('loading');
        document.getElementById('ProductCount')?.classList.remove('loading');
      }
    }
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceGrid = parsed.getElementById('ProductGridContainer');
    const targetGrid = document.getElementById('ProductGridContainer');
    if (sourceGrid && targetGrid) {
      targetGrid.innerHTML = sourceGrid.innerHTML;
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!sourceCount || !container) return;

    const sourceCountRow = sourceCount.closest('.prea-search__count-row');
    const countRow = container.closest('.prea-search__count-row');
    const count = sourceCount.innerHTML;
    if (sourceCountRow && countRow) {
      countRow.classList.toggle(
        'prea-search__count-row--empty',
        sourceCountRow.classList.contains('prea-search__count-row--empty')
      );
    }

    container.innerHTML = count;
    container.dataset.allCount = sourceCount.dataset.allCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const expandedMoreIndices = new Set();
    const openStateByIndex = new Map();
    document.querySelectorAll('#FacetFiltersForm .js-filter').forEach((el) => {
      const idx = el.dataset.index;
      if (!idx) return;
      openStateByIndex.set(idx, el.open);
      if (el.classList.contains('is-expanded-list')) {
        expandedMoreIndices.add(idx);
      }
    });

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event && event.target ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (!target) return;
      const idx = element.dataset.index;
      const wasOpen = openStateByIndex.has(idx) ? openStateByIndex.get(idx) : target.open;
      target.innerHTML = element.innerHTML;
      target.open = wasOpen;
      if (expandedMoreIndices.has(idx)) {
        target.classList.add('is-expanded-list');
        const toggleBtn = target.querySelector('[data-prea-show-more]');
        if (toggleBtn) {
          toggleBtn.setAttribute('aria-expanded', 'true');
          const moreText = toggleBtn.querySelector('[data-prea-show-more-text]');
          const lessText = toggleBtn.querySelector('[data-prea-show-less-text]');
          if (moreText) moreText.hidden = true;
          if (lessText) lessText.hidden = false;
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target) {
      FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    }

    document.dispatchEvent(new CustomEvent('prea:search-facets-updated'));
  }

  static renderActiveFacets(html) {
    const sourceSearchActiveFacets = html.querySelector('.prea-search-filter__active');
    const targetSearchActiveFacets = document.querySelector('.prea-search-filter__active');

    if (sourceSearchActiveFacets && targetSearchActiveFacets) {
      targetSearchActiveFacets.innerHTML = sourceSearchActiveFacets.innerHTML;
    }

    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!activeFacetsElement) return;
      if (!target || target === targetSearchActiveFacets) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
      '.prea-search-filter-panel__footer',
      '.prea-search-filter-panel__header-copy',
      '[data-prea-filter-show-label]',
      '[data-prea-filter-hide-label]'
    ];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      if (!source) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = source.innerHTML;
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileFacetForm ? mobileFacetForm.closest('menu-drawer') : null;
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') {
      mobileDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.outerHTML = sourceElementAccessibility.outerHTML;
    }

    const sourceItems = source.querySelectorAll('.prea-search-filter__item');
    const targetItems = target.querySelectorAll('.prea-search-filter__item');

    if (sourceItems.length === targetItems.length) {
      sourceItems.forEach((srcItem, idx) => {
        const tgtItem = targetItems[idx];
        const srcInput = srcItem.querySelector('.prea-search-filter__checkbox');
        const tgtInput = tgtItem.querySelector('.prea-search-filter__checkbox');
        const srcLabel = srcItem.querySelector('.prea-search-filter__checkbox-label');
        const tgtLabel = tgtItem.querySelector('.prea-search-filter__checkbox-label');
        const srcCount = srcItem.querySelector('.prea-search-filter__count');
        const tgtCount = tgtItem.querySelector('.prea-search-filter__count');

        if (srcCount && tgtCount) {
          tgtCount.textContent = srcCount.textContent;
        }
        if (srcInput && tgtInput) {
          tgtInput.checked = srcInput.checked;
          tgtInput.disabled = srcInput.disabled;
        }
        if (srcLabel && tgtLabel) {
          tgtLabel.classList.toggle('is-disabled', srcLabel.classList.contains('is-disabled'));
          tgtLabel.classList.toggle('is-selected', srcLabel.classList.contains('is-selected'));
        }
        tgtItem.classList.toggle('is-active-extra', srcItem.classList.contains('is-active-extra'));
      });
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return grid?.dataset.id ? [{ section: grid.dataset.id }] : [];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const sortInput = form.querySelector('#SortBy');
    const defaultSort = sortInput ? (sortInput.defaultValue || '') : '';
    const hadSortInUrl = new URLSearchParams(window.location.search).has('sort_by');
    const params = new URLSearchParams();
    const seenValues = new Set();
    for (const [k, v] of formData.entries()) {
      if (v === '' || v === null) continue;
      if (k === 'sort_by' && v === defaultSort && !hadSortInUrl) continue;
      if (k === 'page' || k === 'collection') continue;
      const pair = JSON.stringify([k, v]);
      if (seenValues.has(pair)) continue;
      seenValues.add(pair);
      params.append(k, v);
    }
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement && event.srcElement.className === 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const activeForm = event.target.closest('form');
      const isMobile = activeForm && activeForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.filter(Boolean).join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = new Map();
FacetFiltersForm.requestId = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.getNumberInputs().forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  getNumberInputs() {
    const numberInputs = this.querySelectorAll('input[data-prea-price-input], input[type="number"]');
    return numberInputs.length >= 2 ? numberInputs : this.querySelectorAll('input');
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.getNumberInputs();
    if (inputs.length < 2) return;
    const minInput = inputs[0];
    const maxInput = inputs[1];
    const maxBound = maxInput.dataset.maxBound || maxInput.getAttribute('max');
    if (maxBound && !maxInput.dataset.maxBound) {
      maxInput.dataset.maxBound = maxBound;
    }

    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '' && maxInput.dataset.maxBound) {
      minInput.setAttribute('max', maxInput.dataset.maxBound);
    }
  }

  adjustToValidValues(input) {
    if (input.value === '') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min') || 0);
    const max = Number(input.dataset.maxBound || input.getAttribute('max') || Infinity);

    if (Number.isNaN(value)) {
      input.value = '';
      return;
    }
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    if (event.currentTarget && event.currentTarget.hasAttribute('data-prea-clear-all')) {
      event.stopPropagation();
      FacetFiltersForm.clearAllFilters();
      return;
    }
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
